import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import { REGION_LABELS } from '../constants/regions';

const BookmarksPage = () => {
  const { user, login, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchBookmarks = async () => {
      try {
        const res = await axios.get(`${API_URL}/v1/bookmarks`, { withCredentials: true });
        setBookmarks(res.data.bookmarks);
      } catch (err) {
        console.error('Failed to fetch bookmarks:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBookmarks();
  }, [user, authLoading]);

  const removeBookmark = async (clusterId) => {
    try {
      await axios.delete(`${API_URL}/v1/bookmarks/${clusterId}`, { withCredentials: true });
      setBookmarks(bookmarks.filter(b => b.cluster_id !== clusterId));
    } catch (err) {
      console.error('Failed to remove bookmark:', err);
    }
  };

  if (authLoading || loading) {
    return (
      <>
        <Header />
        <main className="main page-content">
          <div className="loading"><p>Loading bookmarks...</p></div>
        </main>
        <Footer />
      </>
    );
  }

  if (!user) {
    return (
      <>
        <Header />
        <main className="main page-content">
          <div className="bookmarks-page">
            <div className="locked-feature">
              <div className="lock-icon">🔖</div>
              <h1>Your Bookmarks</h1>
              <p>Sign in to save stories and access them later.</p>
              <button className="action-btn primary" onClick={login} data-testid="signin-for-bookmarks">
                Sign In with Google
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="main page-content">
        <div className="bookmarks-page">
          <h1 className="page-title">Your Bookmarks</h1>
          <p className="page-subtitle">Stories you've saved for later.</p>
          
          {bookmarks.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔖</div>
              <h2>No bookmarks yet</h2>
              <p>Start saving stories from the news feed to read later.</p>
              <button className="action-btn primary" onClick={() => navigate('/stories')}>
                Browse Stories
              </button>
            </div>
          ) : (
            <div className="bookmarks-list">
              {bookmarks.map((bookmark) => (
                <div key={bookmark.cluster_id} className="bookmark-card" data-testid={`bookmark-${bookmark.cluster_id}`}>
                  <div className="bookmark-content">
                    <h3 className="bookmark-title">{bookmark.title}</h3>
                    <div className="bookmark-meta">
                      <span>{bookmark.source_count} sources</span>
                      <span>{bookmark.regions?.length || 0} regions</span>
                      <span>Saved {new Date(bookmark.bookmarked_at).toLocaleDateString()}</span>
                    </div>
                    <div className="bookmark-regions">
                      {(bookmark.regions || []).map(r => (
                        <span key={r} className="region-badge">{REGION_LABELS[r] || r}</span>
                      ))}
                    </div>
                  </div>
                  <div className="bookmark-actions">
                    <button 
                      className="action-btn"
                      onClick={() => navigate(`/stories?highlight=${bookmark.cluster_id}`)}
                    >
                      View Story
                    </button>
                    <button 
                      className="action-btn danger"
                      onClick={() => removeBookmark(bookmark.cluster_id)}
                      data-testid={`remove-bookmark-${bookmark.cluster_id}`}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default BookmarksPage;
